import type { BaseSelectionResponse } from "@frend-digital/centra-types/selection";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { selectionChange } from "../../../checkout/actions";
import {
  useCheckoutConfig,
  useCheckoutDispatch,
} from "../../../checkout/selectors";
import { updateAddress } from "../fetchers";
import {
  centraAddressSchema,
  type CentraAddress,
  type CustomAddress,
  type ZodEmptyObject,
} from "../schema";

interface AddressHookProps<
  T extends ZodEmptyObject | undefined,
  Schema extends ZodEmptyObject = typeof centraAddressSchema,
> {
  additionalFields?: T;
  schema?: Schema;
  useCentraSubmit?: boolean;
  onSubmit?: (data: CustomAddress<T, Schema>) => void;

  /**
   *  Callback when address was updated in Centra.
   *  If useCentraSubmit is false, this will not be called.
   */
  onSuccess?: (data: BaseSelectionResponse) => void;

  /**
   *  Callback right before data is submitted to centra.
   *  If added the hook will send this data to centra instead of the original schema data.
   */
  transformData?: (data: z.infer<Schema>) => CentraAddress;
}

export function useAddress<
  T extends ZodEmptyObject | undefined = undefined,
  Schema extends ZodEmptyObject = typeof centraAddressSchema,
>({
  schema,
  additionalFields,
  useCentraSubmit = true,
  onSubmit,
  transformData,
}: AddressHookProps<T, Schema>) {
  const defaultSchema = schema || centraAddressSchema;

  const { centraUrl, token } = useCheckoutConfig();
  const dispatchCheckout = useCheckoutDispatch();
  const finalSchema = additionalFields
    ? z.intersection(defaultSchema, additionalFields)
    : defaultSchema;

  type FormData = z.infer<typeof finalSchema>;

  const form = useForm<CustomAddress<T, Schema>>({
    resolver: zodResolver(finalSchema),
  });

  const mutation = useMutation({
    mutationKey: ["payment-fields", "address", token],
    mutationFn: async (formData: FormData) => {
      const data = transformData ? transformData(formData) : formData;
      // Submit data to centra
      const result = await updateAddress(
        { address: omitFalsy(data) },
        { token, apiUrl: centraUrl },
      );

      return result.unwrap();
    },
    onSuccess: (...args) => {
      const [data] = args;
      dispatchCheckout(selectionChange(data, "address"));
    },
  });

  const handleSubmit = form.handleSubmit(async data => {
    onSubmit?.(data);
    if (useCentraSubmit) {
      mutation.mutate(data);
    }
  });

  return {
    form,
    mutation,
    handleSubmit,
    schema: finalSchema,
    isPending: mutation.isPending,
  };
}

const omitFalsy = <T extends {}>(obj: T) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => !!value),
  ) as T;
};
